import { useWaypoint } from '@fukuri-admins/hooks/common/useWaypoint';
import { useLayoutEffect, useRef, useState } from 'react';
import { FieldWrapper } from '../FieldWrapper';
import {
  ClearableSelectTrigger,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../FormItems';

type FieldWrapperProps = React.ComponentProps<typeof FieldWrapper>;
type SelectTriggerProps = React.ComponentProps<typeof SelectTrigger>;

type SelectPropsBase = {
  placeholder?: string;
  value?: string;
  options: { value: string; label: React.ReactNode }[];
  onValueChange: (value: string) => void;
  clearable?: boolean;
  onBlur?: SelectTriggerProps['onBlur'];
  disabled?: boolean;
};

type SelectPropsWithoutPagination = SelectPropsBase & {
  hasMore?: never;
  onLoadMore?: never;
  isLoading?: never;
};

type SelectPropsWithPagination = SelectPropsBase & {
  /** 次ページが存在するかどうか。false のとき sentinel は描画しない */
  hasMore: boolean;
  /** sentinel がビューポートに入ったときに呼ばれるコールバック */
  onLoadMore: () => void;
  /** データ取得中フラグ（将来的にローディング表示等に利用） */
  isLoading?: boolean;
};

type SelectProps = SelectPropsWithoutPagination | SelectPropsWithPagination;

type Props = {
  select: SelectProps;
  fieldWrapperProps: Omit<FieldWrapperProps, 'children'>;
};

/**
 * セレクトフィールド
 * ページネーション props（hasMore / onLoadMore）を指定すると無限スクロール対応になる。
 * 未指定時は従来通り全件描画。
 */
export const SelectField: React.FC<Props> = ({
  select: {
    placeholder,
    value,
    options,
    onValueChange,
    clearable = false,
    onBlur,
    disabled,
    ...paginationProps
  },
  fieldWrapperProps: { htmlFor, labelType, labelText, beforeMessage, errorMessages },
}) => {
  const hasPagination = 'hasMore' in paginationProps;
  const { hasMore, onLoadMore } = hasPagination
    ? paginationProps
    : { hasMore: undefined, onLoadMore: undefined };

  // Viewport の DOM 要素を state で保持する（ref では再レンダーが走らないため）
  const [viewportEl, setViewportEl] = useState<HTMLDivElement | null>(null);

  // onLoadMore 発火直前の scrollTop を保存し、Item append 後に復元する（Radix issue #2440 対策）
  const savedScrollTopRef = useRef<number | null>(null);

  const handleLoadMore = onLoadMore
    ? () => {
        if (viewportEl) {
          savedScrollTopRef.current = viewportEl.scrollTop;
        }
        onLoadMore();
      }
    : undefined;

  // options が増えた（=データ追加後）に scrollTop を復元する
  useLayoutEffect(() => {
    if (savedScrollTopRef.current !== null && viewportEl) {
      viewportEl.scrollTop = savedScrollTopRef.current;
      savedScrollTopRef.current = null;
    }
  }, [options, viewportEl]);

  const { ref: sentinelRef } = useWaypoint({
    callback: handleLoadMore ?? (() => {}),
    root: viewportEl,
  });

  const shouldRenderSentinel = hasMore && viewportEl !== null;

  return (
    <FieldWrapper
      htmlFor={htmlFor}
      labelType={labelType}
      labelText={labelText}
      beforeMessage={beforeMessage}
      errorMessages={errorMessages}
    >
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        {clearable ? (
          <ClearableSelectTrigger
            onClear={() => onValueChange('')}
            hasValue={!!value}
            onBlur={onBlur}
          >
            <SelectValue placeholder={placeholder} />
          </ClearableSelectTrigger>
        ) : (
          <SelectTrigger onBlur={onBlur}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
        )}
        <SelectContent viewportRef={setViewportEl}>
          <SelectGroup>
            {options.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectGroup>
          {shouldRenderSentinel && <div ref={sentinelRef} aria-hidden />}
        </SelectContent>
      </Select>
    </FieldWrapper>
  );
};
